import {
  BasicStorageItem,
  Logger,
  MethodNotImplementedError,
  ServiceProvider,
  StorageItem,
} from '@uploadcam/common';
import {
  FeedItemUrlMap,
  StorageAdapter,
  StorageCapabilities,
  StorageItemId,
} from '../common/adapter';
import { StorageGetNextListPage, StorageListPage } from '../common/list-page';
import { StorageManager } from '../common/manager';
import { MockItem } from '../common/mock.item';
import { MockListPage } from '../common/mock.list-page';
import { ZohoItem } from './zoho.item';
import { ZohoListPage } from './zoho.list-page';
import { GetAuthDtoInteractor } from '../../../auth/domain/interactors/get-auth-dto.interactor';
import { ZohoApiClient } from './zoho-api.client';
import { FriendlyError } from '../../../../app/core/utils';

export const ZOHO_ROOT = 'uploadcam://zoho/root';

const ZOHO_PAGE_SIZE = 50;

interface ZohoNextPageOptions {
  folderId: string;
  pageOffset: number;
}

export class ZohoAdapter implements StorageAdapter {
  private manager!: StorageGetNextListPage;

  constructor(
    private readonly api: ZohoApiClient,
    private readonly getAuthDto: GetAuthDtoInteractor,
    private readonly logger: Logger,
  ) {}

  public setManager(manager: StorageManager): void {
    this.manager = manager;
  }

  public getCapabilities(): StorageCapabilities {
    return {
      previewThumbnails: true,
      userGroups: false,
    };
  }

  public getProvider(): ServiceProvider {
    return ServiceProvider.Zoho;
  }

  public async getRootFolder(): Promise<StorageItem> {
    return MockItem.rootFolder(ZOHO_ROOT, ServiceProvider.Zoho, undefined, false);
  }

  public async listFolder(
    item: StorageItem,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    if (item.id === ZOHO_ROOT) {
      return this.listTeamFolders();
    }

    return this.fetchFolderPage(item.id, onlyFolders, 0);
  }

  private async fetchFolderPage(
    folderId: string,
    onlyFolders: boolean,
    pageOffset: number,
  ): Promise<ZohoListPage> {
    const auth = await this.getAuthDto.execute();
    const res = await this.api.listFolder({
      auth,
      folderId,
      onlyFolders,
      pageOffset,
      pageLimit: ZOHO_PAGE_SIZE,
    });

    const sorted = [...res.items].sort((a, b) => a.name.localeCompare(b.name));
    const items = sorted.map((entry) => new ZohoItem({ id: entry.id, attributes: entry }, true));

    return new ZohoListPage(
      this.manager,
      items,
      onlyFolders,
      res.hasMore,
      res.nextOffset,
      folderId,
    );
  }

  private async listTeamFolders(): Promise<StorageListPage> {
    const auth = await this.getAuthDto.execute();
    const res = await this.api.listTeamFolders({ auth });

    const items = res.folders.map(
      (folder) => new ZohoItem({ id: folder.id, attributes: folder }, true),
    );

    return new MockListPage(items);
  }

  public async createFolder(parent: StorageItem, name: string): Promise<StorageItem> {
    const auth = await this.getAuthDto.execute();
    const res = await this.api.createFolder({ auth, parentId: parent.id, name });

    return new ZohoItem({ id: res.id, attributes: res.attributes }, true);
  }

  public async renameFolder(folder: StorageItem, name: string): Promise<StorageItem> {
    const auth = await this.getAuthDto.execute();

    // Zoho WorkDrive allows sibling folders with the same name, so we check for
    // a conflict ourselves instead of silently creating a duplicate.
    await this.assertNoFolderNameConflict(folder, name);

    const res = await this.api.renameFolder({ auth, fileId: folder.id, name });

    return new ZohoItem({ id: res.id, attributes: res.attributes }, true);
  }

  private async assertNoFolderNameConflict(folder: StorageItem, name: string): Promise<void> {
    const parentId = folder.parentId;

    // Without a known parent we can't scope the lookup (e.g. a team-folder
    // root); skip the check rather than block the rename.
    if (!parentId) {
      return;
    }

    const auth = await this.getAuthDto.execute();
    let pageOffset = 0;

    // Page through the parent's sub-folders looking for a name clash.
    while (true) {
      const res = await this.api.listFolder({
        auth,
        folderId: parentId,
        onlyFolders: true,
        pageOffset,
        pageLimit: ZOHO_PAGE_SIZE,
      });

      const hasConflict = res.items.some(
        (item) => item.id !== folder.id && item.name === name,
      );

      if (hasConflict) {
        throw new FriendlyError(
          'folderNameConflict',
          new Error(`A folder named "${name}" already exists.`),
        );
      }

      if (!res.hasMore || res.nextOffset === undefined) {
        break;
      }

      pageOffset = res.nextOffset;
    }
  }

  public getProviderUrl(): string {
    return 'https://workdrive.zoho.com';
  }

  public updateAccessToken(_token: string): void {
    // No-op: token management is handled by GetAuthDtoInteractor
  }

  public async getNextFolderPage(onlyFolders: boolean, options: unknown): Promise<StorageListPage> {
    const { folderId, pageOffset } = options as ZohoNextPageOptions;
    return this.fetchFolderPage(folderId, onlyFolders, pageOffset);
  }

  // --- Stub methods not needed for folder picker ---

  public async getThumbnailUrls(items: readonly BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'thumbnail');
  }

  public async getPreviewUrls(items: readonly BasicStorageItem[]): Promise<FeedItemUrlMap> {
    return this.getImageUrls(items, 'preview');
  }

  private async getImageUrls(
    items: readonly BasicStorageItem[],
    type: 'thumbnail' | 'preview',
  ): Promise<FeedItemUrlMap> {
    const auth = await this.getAuthDto.execute();
    const fileIds = items.map((item) => item.id);
    const res = await this.api.getThumbnails({ auth, fileIds, type });

    return res.urls;
  }

  public async getDownloadUrls(items: readonly BasicStorageItem[]): Promise<FeedItemUrlMap> {
    if (items.length === 0) {
      return {};
    }

    const auth = await this.getAuthDto.execute();
    const result: FeedItemUrlMap = {};
    items.forEach((item) => (result[item.id] = undefined));

    const settled = await Promise.allSettled(
      items.map(async (item) => {
        const res = await this.api.getFile({ auth, fileId: item.id });
        return { id: item.id, url: res.attributes.download_url };
      }),
    );

    for (const s of settled) {
      if (s.status === 'fulfilled') {
        result[s.value.id] = s.value.url;
      } else {
        this.logger.error(s.reason);
      }
    }

    return result;
  }

  public async getFileUrl(file: StorageItem): Promise<string> {
    return this.getPermalink(file);
  }

  public async getFolderUrl(folder: StorageItem): Promise<string> {
    return this.getPermalink(folder);
  }

  private async getPermalink(item: StorageItem): Promise<string> {
    const auth = await this.getAuthDto.execute();
    const res = await this.api.getFile({ auth, fileId: item.id });
    const permalink = res.attributes.permalink;

    if (!permalink) {
      throw new Error(`Zoho item ${item.id} has no permalink attribute`);
    }

    return permalink;
  }

  public async write(_parent: StorageItem, _file: File): Promise<StorageItem> {
    throw new MethodNotImplementedError();
  }

  public async moveItems(
    targetFolder: StorageItem,
    items: StorageItem[],
  ): Promise<Record<StorageItemId, boolean>> {
    if (items.length === 0) {
      return {};
    }

    const auth = await this.getAuthDto.execute();
    const res = await this.api.moveFiles({
      auth,
      fileIds: items.map((item) => item.id),
      targetFolderId: targetFolder.id,
    });

    const result: Record<StorageItemId, boolean> = {};
    for (const item of items) {
      result[item.id] = res.results[item.id] ?? false;
    }

    return result;
  }

  public async trashItems(_items: StorageItem[]): Promise<Record<StorageItemId, boolean>> {
    throw new MethodNotImplementedError();
  }

  public async setCaption(_item: Readonly<StorageItem>, _caption?: string): Promise<void> {
    throw new MethodNotImplementedError();
  }
}
